<template>
  <DropdownSelect
      :label="label"
      :modelValue="modelValue"
      :options="completionOptions"
      :maxTextLength="maxTextLength"
      :loading="loading"
      @update:modelValue="handleSelectionChange"
  />
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DropdownSelect from './DropdownSelect.vue';
import reportsService from '@/service/reportsService';

const { t } = useI18n();

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  modelValue: {
    type: [String, Number],
    default: 'all'
  },
  maxTextLength: {
    type: Number,
    default: 15
  },
  tabType: {
    type: String,
    default: 'test'
  }
});

const emit = defineEmits(['update:modelValue', 'optionsLoaded', 'filterChange']);

const completionOptions = ref([]);
const loading = ref(false);
const rawCompletionData = ref([]);

const processCompletionRanges = (data) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  const completionRates = data
      .map(item => {
        return item.completionRate || item.completion || item.progress || 0;
      })
      .filter(rate => rate >= 0 && rate <= 100);

  if (completionRates.length === 0) {
    return [{ value: 'all', label: t('allCompletions') }];
  }

  const minCompletion = Math.min(...completionRates);
  const maxCompletion = Math.max(...completionRates);

  const ranges = [];

  ranges.push({ value: 'all', label: t('completedOnes') });

  ranges.push({ value: 'incomplete', label: t('incompleteRange') });
  ranges.push({ value: 'partial', label: t('partiallyCompletedRange') });
  ranges.push({ value: 'completed', label: t('completedRange') });

  if (maxCompletion >= 50) {
    for (let i = 0; i <= 75; i += 25) {
      const start = i;
      const end = Math.min(i + 25, 100);
      const value = `${start}-${end}`;
      const label = `${start}%-${end}%`;
      ranges.push({ value, label });
    }
  } else {
    for (let i = 0; i <= 40; i += 10) {
      const start = i;
      const end = Math.min(i + 10, 50);
      const value = `${start}-${end}`;
      const label = `${start}%-${end}%`;
      ranges.push({ value, label });
    }
  }

  return ranges;
};

const fetchCompletionData = async () => {
  loading.value = true;

  try {
    const apiTypeMapping = {
      test: 'selfAssessments',
      program: 'programs',
      gamelab: 'gameLabs',
      user: 'users'
    };

    const apiType = apiTypeMapping[props.tabType] || 'selfAssessments';

    const params = {
      type: apiType,
      parameterType: 'properties',
      status: 1,
      page: 1,
      limit: 100
    };

    const response = await reportsService.getReportData(params);

    if (response.data && response.data.data) {
      rawCompletionData.value = response.data.data;

      const ranges = processCompletionRanges(response.data.data);
      completionOptions.value = ranges;

      emit('optionsLoaded', ranges);

    } else {
      completionOptions.value = [
        { value: 'all', label: t('allCompletions') },
        { value: 'incomplete', label: t('incompleteRange') },
        { value: 'partial', label: t('partiallyCompletedRange') },
        { value: 'completed', label: t('completedRange') }
      ];
    }

  } catch (error) {
    completionOptions.value = [
      { value: 'all', label: t('allCompletions') },
      { value: 'incomplete', label: t('incompleteRange') },
      { value: 'partial', label: t('partiallyCompletedRange') },
      { value: 'completed', label: t('completedRange') }
    ];

    emit('optionsLoaded', completionOptions.value);

  } finally {
    loading.value = false;
  }
};

const handleSelectionChange = (selectedValue) => {
  let completionParams = null;

  if (selectedValue && selectedValue !== 'all') {
    if (selectedValue === 'incomplete') {
      completionParams = { min: 0, max: 25 };
    } else if (selectedValue === 'partial') {
      completionParams = { min: 26, max: 75 };
    } else if (selectedValue === 'completed') {
      completionParams = { min: 76, max: 100 };
    } else if (selectedValue.includes('-')) {
      const [min, max] = selectedValue.split('-').map(num => parseInt(num));
      completionParams = {
        min: min || 0,
        max: max || 100
      };
    }
  }

  emit('update:modelValue', selectedValue);

  emit('filterChange', {
    completion: completionParams,
    selectedRange: selectedValue
  });
};

watch(() => props.tabType, async (newTabType) => {
  if (newTabType) {
    await fetchCompletionData();
  }
});

onMounted(async () => {
  await fetchCompletionData();
});

defineExpose({
  refreshData: fetchCompletionData,
  getCompletionData: () => rawCompletionData.value
});
</script>
